import Button from '@atoms/Button'
import Icon from '@components/utils/Icon'
import { blastStatusLabel } from '@components/atoms/BlastStatusBadge'
import { scopedTranslation } from '@utils/I18n'

import type { BlastStatus } from '../../types'

const t = scopedTranslation('comms_management.blasts_index.active_filters')

type Chip = { key: string; label: string; onRemove: () => void }

export function BlastActiveFilters({
  keyword,
  groups,
  statuses,
  onRemoveStatus,
  onRemoveGroup,
  onRemoveKeyword,
  onClear,
}: {
  keyword: string | null
  groups: { id: string; name: string }[]
  statuses: BlastStatus[]
  onRemoveStatus: (status: BlastStatus) => void
  onRemoveGroup: (id: string) => void
  onRemoveKeyword: () => void
  onClear: () => void
}) {
  const chips: Chip[] = [
    ...statuses.map((status) => ({
      key: `status-${status}`,
      label: t('status', { status: blastStatusLabel(status) }),
      onRemove: () => onRemoveStatus(status),
    })),
    ...groups.map((group) => ({
      key: `group-${group.id}`,
      label: t('group', { group: group.name }),
      onRemove: () => onRemoveGroup(group.id),
    })),
    ...(keyword ? [{ key: 'keyword', label: t('keyword', { keyword }), onRemove: onRemoveKeyword }] : []),
  ]

  if (chips.length === 0) return null

  return (
    <div className="tw:flex tw:justify-between tw:gap-4 tw:border-b tw:border-gray-300 tw:px-6 tw:py-4">
      <div className="tw:flex tw:h-fit tw:flex-auto tw:flex-wrap tw:justify-start tw:gap-3">
        <h2 className="tw:sr-only">{t('title')}</h2>
        {chips.map((chip) => (
          <div
            key={chip.key}
            className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-lg tw:bg-primary-50 tw:px-4 tw:py-3 tw:text-sm tw:font-medium tw:text-primary-700"
          >
            {chip.label}
            <button onClick={chip.onRemove} aria-label={t('remove', { filter: chip.label })}>
              <Icon type="x-close" width={10} height={10} aria-hidden />
            </button>
          </div>
        ))}
      </div>

      <div className="tw:flex tw:w-min tw:flex-auto tw:flex-wrap tw:justify-end tw:gap-2">
        <Button leadingIcon="refresh" onClick={onClear}>
          {t('clear')}
        </Button>
      </div>
    </div>
  )
}
